import type { Event } from '@/api/endpoints/bars';

export type EventWithBar = Event & { barName?: string; barId?: string; distance?: number };

/**
 * Extract LOCAL time (HH:MM) from ISO datetime for sorting.
 * Converts UTC times to local timezone before extracting time.
 * Returns "99:99" for null/invalid to sort them last.
 */
export function getTimeForSort(datetime: string | null): string {
  if (!datetime) return '99:99';

  try {
    // Parse the ISO datetime and convert to local time
    const date = new Date(datetime);
    if (isNaN(date.getTime())) return '99:99';

    // Get local hours and minutes, pad to 2 digits
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    return `${hours}:${minutes}`;
  } catch {
    return '99:99';
  }
}

/**
 * Sort events by time only (earliest first).
 * Events without time sort to the end.
 */
export function sortEventsByTime(events: EventWithBar[]): EventWithBar[] {
  return [...events].sort((a, b) => {
    const aTime = getTimeForSort(a.startDatetime);
    const bTime = getTimeForSort(b.startDatetime);
    return aTime.localeCompare(bTime);
  });
}

/**
 * Sort events by distance (closest first), with time as secondary sort.
 * Events at the same distance are sorted by start time.
 */
export function sortEventsByDistance(events: EventWithBar[]): EventWithBar[] {
  return [...events].sort((a, b) => {
    const distanceDiff = (a.distance ?? Infinity) - (b.distance ?? Infinity);
    if (distanceDiff !== 0) return distanceDiff;

    const aTime = getTimeForSort(a.startDatetime);
    const bTime = getTimeForSort(b.startDatetime);
    return aTime.localeCompare(bTime);
  });
}
